import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  GitBranch, Plus, Play, Copy, Trash2, Clock, DollarSign,
  CheckCircle2, XCircle, MoreHorizontal, PlayCircle, AlertCircle, Sparkles,
  Settings2, Bell, UserPlus, X, Zap
} from "lucide-react";
import { workflowsApi, runsApi } from "../../lib/workflows";
import api from "../../lib/api";

export default function Workflows() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ active: 0, waiting: 0, completed_today: 0, failed_today: 0 });
  const [templates, setTemplates] = useState([]);
  const [community, setCommunity] = useState([]);
  const [usingSlug, setUsingSlug] = useState(null);
  const [busyWid, setBusyWid] = useState(null);
  const [settingsWf, setSettingsWf] = useState(null);
  const [sharedWithMe, setSharedWithMe] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [w, s, t, c, sm] = await Promise.all([
        workflowsApi.list(),
        runsApi.summary(),
        api.get("/workflow_templates").then((r) => r.data),
        api.get("/workflow_templates/community").then((r) => r.data).catch(() => ({ templates: [] })),
        api.get("/team/shared-with-me").then((r) => r.data).catch(() => ({ workflows: [] })),
      ]);
      setItems(w.workflows || []);
      setSummary(s);
      setTemplates(t.templates || []);
      setCommunity(c.templates || []);
      setSharedWithMe(sm.workflows || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const publish = async (wf) => {
    setBusyWid(wf.workflow_id);
    try {
      if (wf.is_public) {
        await api.post(`/workflow_templates/unpublish/${wf.workflow_id}`);
      } else {
        await api.post(`/workflow_templates/publish/${wf.workflow_id}`);
      }
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally { setBusyWid(null); }
  };

  const cloneCommunity = async (wid) => {
    setBusyWid(wid);
    try {
      const r = await api.post(`/workflow_templates/community/${wid}/clone`).then((x) => x.data);
      nav(`/workforce/workflows/${r.workflow_id}`);
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally { setBusyWid(null); }
  };

  const startFromTemplate = async (slug) => {
    setUsingSlug(slug);
    try {
      const r = await api.post(`/workflow_templates/${slug}/use`).then((x) => x.data);
      nav(`/workforce/workflows/${r.workflow_id}`);
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally {
      setUsingSlug(null);
    }
  };

  const createNew = async () => {
    const wf = await workflowsApi.create({
      name: "Untitled workflow",
      description: "",
      nodes: [
        { node_id: "start_1",  type: "start",  position: { x: 80,  y: 200 }, config: {} },
        { node_id: "output_1", type: "output", position: { x: 720, y: 200 }, config: { format: "markdown" } },
      ],
      edges: [
        { edge_id: "e1", source: "start_1", target: "output_1" },
      ],
      variables: [],
      settings: { max_cost: 2, max_nodes: 50 },
    });
    nav(`/workforce/workflows/${wf.workflow_id}`);
  };

  const duplicate = async (id, e) => {
    e.stopPropagation();
    const dup = await workflowsApi.duplicate(id);
    load();
    nav(`/workforce/workflows/${dup.workflow_id}`);
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this workflow?")) return;
    await workflowsApi.remove(id);
    load();
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="workflows-page">
      <div className="flex items-center gap-2 mb-1">
        <GitBranch size={20} strokeWidth={2.5} />
        <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Workflows</div>
      </div>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold mb-1">My Workflows</h1>
          <p className="text-slate-600 max-w-2xl">
            Connect workers, debates, reviewers and human approvals into repeatable AI processes.
          </p>
        </div>
        <button onClick={createNew} className="brut-btn" data-testid="new-workflow-btn">
          <Plus size={16} strokeWidth={2.5} /> Create Workflow
        </button>
      </div>

      {/* Command-center strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="command-center">
        <StatCard icon={PlayCircle} label="Active runs" value={summary.active} color="#3B82F6" />
        <StatCard icon={AlertCircle} label="Waiting" value={summary.waiting} color="#F59E0B" />
        <StatCard icon={CheckCircle2} label="Completed today" value={summary.completed_today} color="#10B981" />
        <StatCard icon={XCircle} label="Failed today" value={summary.failed_today} color="#EF4444" />
      </div>

      {loading ? (
        <div className="text-sm text-slate-400 italic">Loading…</div>
      ) : (
        <>
          {templates.length > 0 && (
            <div className="mb-8" data-testid="template-gallery">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles size={14} strokeWidth={2.5} className="text-amber-500" />
                <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Start from a template</div>
              </div>
              <div className="grid md:grid-cols-3 gap-3">
                {templates.map((t) => (
                  <button
                    key={t.slug}
                    onClick={() => startFromTemplate(t.slug)}
                    disabled={usingSlug === t.slug}
                    className="brut-card p-4 text-left hover:-translate-y-1 hover:translate-x-0 transition-transform"
                    data-testid={`use-template-${t.slug}`}
                  >
                    <div className="flex items-start gap-2 mb-1">
                      <div className="text-2xl leading-none">{t.emoji}</div>
                      <div className="min-w-0">
                        <div className="font-heading font-bold text-base leading-tight">{t.name}</div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                          {t.node_count} nodes · ~${t.estimated_cost.toFixed(2)}
                        </div>
                      </div>
                    </div>
                    <div className="text-xs text-slate-600 leading-relaxed mb-2 line-clamp-2">{t.description}</div>
                    <div className="flex flex-wrap gap-1">
                      {t.has_reviewer && <MiniTag color="#8B5CF6">Reviewer</MiniTag>}
                      {t.has_debate   && <MiniTag color="#EF4444">Debate</MiniTag>}
                      {t.has_approval && <MiniTag color="#EC4899">Approval</MiniTag>}
                    </div>
                    <div className="mt-2 text-xs font-heading font-bold text-slate-900">
                      {usingSlug === t.slug ? "Creating…" : "Use →"}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div data-testid="workflow-grid" className="grid md:grid-cols-2 gap-4">
            {items.length === 0
              ? <div className="col-span-full"><EmptyState onCreate={createNew} /></div>
              : items.map((w) => (
                  <WorkflowCard key={w.workflow_id} w={w}
                    onOpen={() => nav(`/workforce/workflows/${w.workflow_id}`)}
                    onDuplicate={(e) => duplicate(w.workflow_id, e)}
                    onDelete={(e) => remove(w.workflow_id, e)}
                    onPublish={(e) => { e.preventDefault(); e.stopPropagation(); publish(w); }}
                    onSettings={(e) => { e.preventDefault(); e.stopPropagation(); setSettingsWf(w); }}
                    publishBusy={busyWid === w.workflow_id}
                  />
                ))}
          </div>

          {community.length > 0 && (
            <div className="mt-10" data-testid="community-gallery">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles size={14} strokeWidth={2.5} className="text-emerald-500" />
                <div className="text-xs uppercase tracking-widest font-bold text-slate-500">From the community</div>
                <span className="text-[10px] text-slate-400 ml-1">Published by other builders — one click to fork.</span>
              </div>
              <div className="grid md:grid-cols-3 gap-3">
                {community.map((c) => (
                  <button
                    key={c.workflow_id}
                    onClick={() => cloneCommunity(c.workflow_id)}
                    disabled={busyWid === c.workflow_id}
                    className="brut-card p-4 text-left hover:-translate-y-1 transition-transform"
                    data-testid={`community-clone-${c.workflow_id}`}
                    style={{ borderColor: "#059669", boxShadow: "4px 4px 0 #059669" }}
                  >
                    <div className="flex items-start gap-2 mb-1">
                      <div className="text-2xl leading-none">🌱</div>
                      <div className="min-w-0 flex-1">
                        <div className="font-heading font-bold text-base leading-tight truncate">{c.name}</div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                          by {c.publisher_name} · {c.node_count} nodes
                        </div>
                      </div>
                      <div className="text-[10px] font-heading font-bold text-emerald-700 whitespace-nowrap">🍴 {c.forks || 0}</div>
                    </div>
                    <div className="text-xs text-slate-600 leading-relaxed mb-2 line-clamp-2">{c.description || "—"}</div>
                    <div className="flex flex-wrap gap-1">
                      {c.has_reviewer && <MiniTag color="#8B5CF6">Reviewer</MiniTag>}
                      {c.has_debate   && <MiniTag color="#EF4444">Debate</MiniTag>}
                      {c.has_approval && <MiniTag color="#EC4899">Approval</MiniTag>}
                      {c.has_router   && <MiniTag color="#7C3AED">Router</MiniTag>}
                      {c.has_tool     && <MiniTag color="#64748B">Tool</MiniTag>}
                    </div>
                    <div className="mt-2 text-xs font-heading font-bold text-emerald-800">
                      {busyWid === c.workflow_id ? "Forking…" : "Fork →"}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {sharedWithMe.length > 0 && (
            <div className="mt-10" data-testid="shared-with-me">
              <div className="flex items-center gap-2 mb-3">
                <UserPlus size={14} strokeWidth={2.5} className="text-amber-600" />
                <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Shared with you</div>
              </div>
              <div className="grid md:grid-cols-3 gap-3">
                {sharedWithMe.map((s) => (
                  <button
                    key={s.workflow_id}
                    onClick={() => nav(`/workforce/workflows/${s.workflow_id}`)}
                    className="brut-card p-4 text-left hover:-translate-y-1 transition-transform"
                    data-testid={`shared-${s.workflow_id}`}
                    style={{ borderColor: "#D97706", boxShadow: "4px 4px 0 #D97706" }}
                  >
                    <div className="flex items-start gap-2 mb-1">
                      <div className="text-2xl leading-none">🤝</div>
                      <div className="min-w-0 flex-1">
                        <div className="font-heading font-bold text-base leading-tight truncate">{s.name}</div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                          by {s.owner_name} · {s.role}
                        </div>
                      </div>
                    </div>
                    <div className="text-xs text-slate-600 leading-relaxed line-clamp-2">{s.description || "—"}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <div className="mt-6 text-sm">
        <Link to="/workforce/runs" className="font-heading font-bold text-slate-900 hover:text-amber-600 border-b-2 border-slate-900 hover:border-amber-500" data-testid="see-all-runs">
          See all runs →
        </Link>
      </div>

      {settingsWf && (
        <SettingsModal
          wf={settingsWf}
          onClose={() => setSettingsWf(null)}
          onSaved={async () => { await load(); const fresh = (await workflowsApi.get(settingsWf.workflow_id)); setSettingsWf(fresh); }}
        />
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <Link to="/workforce/runs" className="brut-card p-4 flex items-center gap-3" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="w-10 h-10 rounded-xl border-2 border-slate-900 flex items-center justify-center shrink-0"
           style={{ backgroundColor: color + "22", color }}>
        <Icon size={18} strokeWidth={2.5} />
      </div>
      <div>
        <div className="font-heading font-bold text-2xl leading-none">{value}</div>
        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mt-1">{label}</div>
      </div>
    </Link>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="brut-card p-8 md:p-12 text-center max-w-2xl mx-auto" data-testid="empty-state">
      <div className="text-5xl mb-3">🧩</div>
      <h2 className="font-heading text-2xl md:text-3xl font-bold mb-2">Build your first AI workflow</h2>
      <p className="text-slate-600 mb-6 max-w-md mx-auto">
        Connect workers, debates, reviewers, tools and human decisions into repeatable AI processes.
      </p>
      <button onClick={onCreate} className="brut-btn" data-testid="empty-cta">
        <Plus size={16} strokeWidth={2.5} /> Create Workflow
      </button>
    </div>
  );
}

function WorkflowCard({ w, onOpen, onDuplicate, onDelete, onPublish, onSettings, publishBusy }) {
  const workerCount = (w.nodes || []).filter((n) => n.type === "worker").length;
  const stats = w.stats || { runs: 0, success: 0, failed: 0, total_cost: 0 };
  const successRate = stats.runs > 0 ? Math.round((stats.success / stats.runs) * 100) : null;
  const scheduleActive = w.schedule && w.schedule.enabled;
  return (
    <div
      onClick={onOpen}
      className="brut-card p-5 cursor-pointer hover:-translate-y-1 hover:translate-x-0 transition-transform"
      data-testid={`workflow-card-${w.workflow_id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="font-heading text-lg font-bold leading-tight flex-1 min-w-0">{w.name}</div>
        <div className="flex items-center gap-1 shrink-0">
          {scheduleActive && (
            <span className="text-[10px] font-heading font-bold px-1.5 py-0.5 rounded-full bg-blue-100 border border-blue-500 text-blue-800" title={`Cron: ${w.schedule.cron}`} data-testid={`sched-badge-${w.workflow_id}`}>
              ⏰ SCHEDULED
            </span>
          )}
          {w.is_public && (
            <span className="text-[10px] font-heading font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 border border-emerald-500 text-emerald-800" data-testid={`public-badge-${w.workflow_id}`}>
              PUBLIC · 🍴 {stats.forks || 0}
            </span>
          )}
          <button onClick={onSettings} className="p-1.5 text-slate-400 hover:text-slate-900" title="Schedule / Notify / Share" data-testid={`settings-${w.workflow_id}`}>
            <Settings2 size={14} strokeWidth={2.5} />
          </button>
          <button onClick={onPublish} disabled={publishBusy}
                  className={`p-1.5 ${w.is_public ? "text-emerald-600 hover:text-slate-900" : "text-slate-400 hover:text-emerald-600"}`}
                  title={w.is_public ? "Unpublish" : "Publish to community"} data-testid={`publish-${w.workflow_id}`}>
            {publishBusy ? "…" : (w.is_public ? "📢" : "🌱")}
          </button>
          <button onClick={onDuplicate} className="p-1.5 text-slate-400 hover:text-slate-900" title="Duplicate" data-testid={`dup-${w.workflow_id}`}>
            <Copy size={14} strokeWidth={2.5} />
          </button>
          <button onClick={onDelete} className="p-1.5 text-slate-400 hover:text-red-500" title="Delete" data-testid={`del-${w.workflow_id}`}>
            <Trash2 size={14} strokeWidth={2.5} />
          </button>
        </div>
      </div>
      {w.description && <p className="text-sm text-slate-600 mb-3 line-clamp-2">{w.description}</p>}

      <div className="flex flex-wrap gap-1.5 mb-3">
        <Chip>{(w.nodes || []).length} nodes</Chip>
        <Chip>{workerCount} worker{workerCount === 1 ? "" : "s"}</Chip>
        {successRate !== null && <Chip>{successRate}% success</Chip>}
      </div>

      <div className="flex items-center justify-between text-xs text-slate-500 font-mono">
        <span>{stats.runs} run{stats.runs === 1 ? "" : "s"}</span>
        <span>${(stats.total_cost || 0).toFixed(4)}</span>
        <span>{w.last_run_at ? relDate(w.last_run_at) : "never run"}</span>
      </div>
    </div>
  );
}

function Chip({ children }) {
  return (
    <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full border-2 border-slate-900 bg-white">
      {children}
    </span>
  );
}

function MiniTag({ color, children }) {
  return (
    <span
      className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full border"
      style={{ borderColor: color, color, backgroundColor: color + "1F" }}
    >
      {children}
    </span>
  );
}

function relDate(iso) {
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch { return ""; }
}



/* -------------------- Settings Modal -------------------- */
function TriggerTab({ wf }) {
  const [hook, setHook] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState("");
  const generate = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/workflows/${wf.workflow_id}/webhook-token`); setHook(data); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  const revoke = async () => {
    if (!window.confirm("Revoke this trigger URL? Zaps using it will stop working.")) return;
    await api.delete(`/workflows/${wf.workflow_id}/webhook-token`); setHook(null);
  };
  const copy = (k, v) => { navigator.clipboard.writeText(v); setCopied(k); setTimeout(() => setCopied(""), 1200); };
  const url = hook?.webhook_url;
  return (
    <div className="space-y-3" data-testid="trigger-tab">
      <p className="text-xs text-slate-500 leading-relaxed">
        Start this workflow from <b>Zapier</b>, <b>Make</b>, n8n or any HTTP client and read the result back in one step. The URL is the secret — anyone with it can trigger runs.
      </p>
      {!url ? (
        <button onClick={generate} disabled={busy} className="brut-btn text-sm" data-testid="trigger-generate">
          <Zap size={12} strokeWidth={3} /> {busy ? "Generating…" : "Generate trigger URL"}
        </button>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <code className="brut-input flex-1 font-mono text-[11px] bg-slate-50 truncate" data-testid="trigger-url">{url}</code>
            <button onClick={() => copy("url", url)} className="brut-btn text-xs" data-testid="trigger-copy">{copied === "url" ? "Copied" : "Copy"}</button>
          </div>
          <Snippet label="Sync — get the answer inline (Zapier 'Webhooks → Custom Request')" k="sync" copied={copied} copy={copy}
                   code={`POST ${url}?wait=60\nContent-Type: application/json\n\n{"input": "{{zap_step_value}}"}\n\n→ { "status": "completed", "final_output": "…", "total_cost": 0.01, "run_id": "run_…", "poll_url": "…" }`} />
          <Snippet label="Long runs — poll until done" k="poll" copied={copied} copy={copy}
                   code={`GET ${url.split("/webhooks/workflow/")[0]}/webhooks/runs/{run_id}?token=${hook.token}\n\n→ { "done": true, "status": "completed", "final_output": "…" }`} />
          <div className="text-[11px] text-slate-500">
            Fields: <code>status</code> (completed · failed · waiting_human · running), <code>done</code>, <code>final_output</code>, <code>total_cost</code>, <code>error</code>, <code>pending_approval</code>.
          </div>
          <button onClick={revoke} className="text-red-500 hover:text-red-700 text-xs font-bold inline-flex items-center gap-1" data-testid="trigger-revoke">
            <Trash2 size={12} strokeWidth={2.5} /> Revoke trigger URL
          </button>
        </>
      )}
    </div>
  );
}

function Snippet({ label, code, k, copied, copy }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-[10px] uppercase tracking-widest font-heading font-bold text-slate-500">{label}</label>
        <button onClick={() => copy(k, code)} className="text-[10px] font-bold text-slate-500 hover:text-slate-900" data-testid={`trigger-copy-${k}`}>{copied === k ? "Copied" : "Copy"}</button>
      </div>
      <pre className="text-[10px] font-mono bg-slate-900 text-emerald-200 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-all">{code}</pre>
    </div>
  );
}

function SettingsModal({ wf, onClose, onSaved }) {
  const [tab, setTab] = useState("schedule");
  const [sched, setSched] = useState(wf.schedule || { enabled: false, cron: "0 9 * * *", input: "" });
  const [notify, setNotify] = useState((wf.settings || {}).notify || { slack_webhook_url: "", discord_webhook_url: "" });
  const [shareEmail, setShareEmail] = useState("");
  const [shareRole, setShareRole] = useState("editor");
  const [members, setMembers] = useState({ owner: null, members: [], your_role: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/workflows/${wf.workflow_id}/members`).then((r) => setMembers(r.data)).catch(() => {});
  }, [wf.workflow_id]);

  const saveSchedule = async () => {
    setBusy(true);
    try {
      await api.put(`/workflows/${wf.workflow_id}/schedule`, sched);
      await onSaved();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  const saveNotify = async () => {
    setBusy(true);
    try {
      const merged = { ...(wf.settings || {}), notify };
      await api.put(`/workflows/${wf.workflow_id}`, { ...wf, settings: merged });
      await onSaved();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  const [outboundTest, setOutboundTest] = useState(null);
  const testOutbound = async () => {
    setBusy(true); setOutboundTest(null);
    try {
      const { data } = await api.post("/workspace/outbound/test", { url: notify.outbound_webhook_url });
      setOutboundTest(data);
    } catch (e) { setOutboundTest({ ok: false, error: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };
  const invite = async () => {
    if (!shareEmail.trim()) return;
    setBusy(true);
    try {
      await api.post(`/workflows/${wf.workflow_id}/share`, { email: shareEmail.trim(), role: shareRole });
      setShareEmail("");
      const r = await api.get(`/workflows/${wf.workflow_id}/members`);
      setMembers(r.data);
    } catch (e) { alert(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  const revoke = async (uid) => {
    setBusy(true);
    try {
      await api.delete(`/workflows/${wf.workflow_id}/share/${uid}`);
      const r = await api.get(`/workflows/${wf.workflow_id}/members`);
      setMembers(r.data);
    } finally { setBusy(false); }
  };
  const changeRole = async (uid, role) => {
    setBusy(true);
    try {
      await api.patch(`/workflows/${wf.workflow_id}/share/${uid}`, { role });
      const r = await api.get(`/workflows/${wf.workflow_id}/members`);
      setMembers(r.data);
    } catch (e) { alert(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="settings-modal">
      <div className="brut-card p-0 bg-white max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-5 py-3 border-b-2 border-slate-900">
          <Settings2 size={18} strokeWidth={2.5} />
          <div className="font-heading font-bold text-base">{wf.name}</div>
          <button onClick={onClose} className="ml-auto text-slate-400 hover:text-slate-900" data-testid="settings-close"><X size={16} strokeWidth={2.5} /></button>
        </div>
        <div className="flex border-b-2 border-slate-900">
          {["schedule", "notify", "trigger", "share"].map((t) => (
            <button key={t} onClick={() => setTab(t)}
                    className={`flex-1 px-4 py-2.5 text-xs uppercase tracking-widest font-heading font-bold ${tab === t ? "bg-amber-100 text-slate-900" : "bg-white text-slate-500 hover:bg-amber-50"}`}
                    data-testid={`tab-${t}`}>
              {t === "schedule" ? <><Clock size={12} strokeWidth={2.5} className="inline mr-1" /> Schedule</> :
               t === "notify" ? <><Bell size={12} strokeWidth={2.5} className="inline mr-1" /> Notify</> :
               t === "trigger" ? <><Zap size={12} strokeWidth={2.5} className="inline mr-1" /> Trigger</> :
               <><UserPlus size={12} strokeWidth={2.5} className="inline mr-1" /> Share</>}
            </button>
          ))}
        </div>
        <div className="p-5 overflow-y-auto flex-1">
          {tab === "trigger" && <TriggerTab wf={wf} />}
          {tab === "schedule" && (
            <div className="space-y-3" data-testid="schedule-tab">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={sched.enabled} onChange={(e) => setSched({ ...sched, enabled: e.target.checked })} data-testid="sched-enabled" />
                <span className="font-heading font-bold">Enable schedule</span>
              </label>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-heading font-bold text-slate-500 block mb-1">Cron expression</label>
                <input value={sched.cron} onChange={(e) => setSched({ ...sched, cron: e.target.value })}
                       placeholder="0 9 * * *  (every day at 09:00 UTC)"
                       className="brut-input text-sm font-mono" data-testid="sched-cron" />
                <div className="text-[10px] text-slate-500 mt-1">Examples: <code>0 9 * * *</code> daily 9am · <code>*/15 * * * *</code> every 15 min · <code>0 9 * * 1</code> Mondays 9am · or <code>daily</code>/<code>hourly</code></div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-heading font-bold text-slate-500 block mb-1">Default input</label>
                <textarea rows={3} value={sched.input} onChange={(e) => setSched({ ...sched, input: e.target.value })}
                          placeholder="What should the workflow receive as its input each run?"
                          className="brut-input text-sm" data-testid="sched-input" />
              </div>
              <button onClick={saveSchedule} disabled={busy} className="brut-btn text-sm" data-testid="save-schedule">
                {busy ? "Saving…" : "Save schedule"}
              </button>
            </div>
          )}
          {tab === "notify" && (
            <div className="space-y-3" data-testid="notify-tab">
              <p className="text-xs text-slate-500 leading-relaxed">On every run completion (success or failure), a compact card is posted to these webhooks.</p>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-heading font-bold text-slate-500 block mb-1">Slack incoming webhook URL</label>
                <input value={notify.slack_webhook_url || ""} onChange={(e) => setNotify({ ...notify, slack_webhook_url: e.target.value })}
                       placeholder="https://hooks.slack.com/services/…" className="brut-input text-sm font-mono" data-testid="notify-slack" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-heading font-bold text-slate-500 block mb-1">Discord webhook URL</label>
                <input value={notify.discord_webhook_url || ""} onChange={(e) => setNotify({ ...notify, discord_webhook_url: e.target.value })}
                       placeholder="https://discord.com/api/webhooks/…" className="brut-input text-sm font-mono" data-testid="notify-discord" />
              </div>
              <div className="pt-3 border-t-2 border-dashed border-slate-200">
                <label className="text-[10px] uppercase tracking-widest font-heading font-bold text-slate-500 block mb-1">Zapier / Make webhook URL (on finish → POST JSON)</label>
                <div className="flex gap-2">
                  <input value={notify.outbound_webhook_url || ""} onChange={(e) => setNotify({ ...notify, outbound_webhook_url: e.target.value })}
                         placeholder="https://hooks.zapier.com/hooks/catch/… or https://hook.eu1.make.com/…" className="brut-input text-sm font-mono flex-1" data-testid="notify-outbound" />
                  <button onClick={testOutbound} disabled={busy || !(notify.outbound_webhook_url || "").startsWith("http")}
                          className="brut-btn text-xs bg-white text-slate-900 disabled:opacity-40" data-testid="notify-outbound-test">Send test</button>
                </div>
                {outboundTest && (
                  <div className={`text-xs mt-1 font-mono ${outboundTest.ok ? "text-emerald-600" : "text-red-600"}`} data-testid="notify-outbound-result">
                    {outboundTest.ok ? `Delivered · HTTP ${outboundTest.status_code}` : `Failed · ${outboundTest.status_code || ""} ${outboundTest.error || ""}`}
                  </div>
                )}
                <p className="text-[11px] text-slate-500 mt-1">
                  Fires <code>run.completed</code>, <code>run.failed</code> and <code>run.waiting_approval</code> with run id, status, input, final output and cost. A workspace-wide default lives in Workforce → Settings → Outbound.
                </p>
              </div>
              <button onClick={saveNotify} disabled={busy} className="brut-btn text-sm" data-testid="save-notify">
                {busy ? "Saving…" : "Save webhooks"}
              </button>
            </div>
          )}
          {tab === "share" && (
            <div className="space-y-3" data-testid="share-tab">
              <p className="text-xs text-slate-500 leading-relaxed">
                {members.your_role === "owner"
                  ? "As the owner, you have full control. Invite teammates by their AiMedley email."
                  : `Your role: ${members.your_role}.`}
              </p>
              <div className="flex gap-2">
                <input value={shareEmail} onChange={(e) => setShareEmail(e.target.value)} placeholder="teammate@example.com" className="brut-input text-sm flex-1" data-testid="share-email" />
                <select value={shareRole} onChange={(e) => setShareRole(e.target.value)} className="brut-input text-sm" data-testid="share-role">
                  <option value="viewer">Viewer</option>
                  <option value="editor">Editor</option>
                  <option value="admin">Admin</option>
                </select>
                <button onClick={invite} disabled={busy} className="brut-btn text-sm" data-testid="share-invite">
                  <UserPlus size={12} strokeWidth={2.5} /> Invite
                </button>
              </div>
              <div className="space-y-1.5" data-testid="member-list">
                {members.owner && (
                  <div className="flex items-center gap-2 text-sm px-2 py-1.5 rounded-lg bg-amber-50 border border-amber-200">
                    <span className="font-heading font-bold">👑 {members.owner.name || members.owner.email}</span>
                    <span className="ml-auto text-[10px] uppercase tracking-widest font-heading font-bold text-amber-700">OWNER</span>
                  </div>
                )}
                {(members.members || []).map((m) => (
                  <div key={m.user_id} className="flex items-center gap-2 text-sm px-2 py-1.5 rounded-lg border border-slate-200" data-testid={`member-${m.user_id}`}>
                    <span>{m.name || m.email}</span>
                    <select
                      value={m.role}
                      onChange={(e) => changeRole(m.user_id, e.target.value)}
                      disabled={busy || members.your_role !== "owner" && members.your_role !== "admin"}
                      className="text-[10px] uppercase tracking-widest font-heading font-bold text-slate-700 bg-slate-100 border border-slate-300 rounded px-1.5 py-0.5"
                      data-testid={`role-select-${m.user_id}`}
                    >
                      <option value="viewer">viewer</option>
                      <option value="editor">editor</option>
                      <option value="admin">admin</option>
                    </select>
                    <button onClick={() => revoke(m.user_id)} className="ml-auto text-red-500 hover:text-red-700 text-xs" data-testid={`revoke-${m.user_id}`}>
                      Revoke
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
